import React, { createContext, useContext, useState } from "react";

type Rg = { number: string; uf: string; org: string; date: string };

type Address = {
  zipCode: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  number: string;
  complement: string;
};
type BankAccount = {
  bank: string;
  agency: string;
  account: string;
  accountType: string;
};

type CustomerData = {
  id: string;
  name: string;
  email: string;
  phone: string;
  cpf: string;
  birthDate: string;
  rg?: Rg;
  address?: Address;
  bankAccount?: BankAccount;
};

type Authorization = {
  id: string;
  link: string;
  expirationDate: string;
};

type ProposalData = {
  idSimulation: string;
  value: number;
  prazo: number;
  customerId: string;
  contract?: string;
  formalizationUrl?: string;
};

type C6BankContextType = {
  customerData: Partial<CustomerData>;
  setCustomerData: (data: Partial<CustomerData>) => void;
  clearCustomerData: () => void;
  proposalData: Partial<ProposalData>;
  setProposalData: (data: Partial<ProposalData>) => void;
  clearProposalData: () => void;
  authorization: Partial<Authorization>;
  setAuthorization: (data: Partial<Authorization>) => void;
  clearAuthorization: () => void;
};

const C6BankContext = createContext<C6BankContextType | undefined>(undefined);

export const C6BankProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [customerData, setCustomerDataState] = useState<Partial<CustomerData>>(
    {},
  );
  const [proposalData, setProposalDataState] = useState<Partial<ProposalData>>(
    {},
  );
  const [authorization, setAuthorizationState] = useState<
    Partial<Authorization>
  >({});

  const setCustomerData = (data: Partial<CustomerData>) => {
    setCustomerDataState((prev) => ({ ...prev, ...data }));
  };

  const clearCustomerData = () => setCustomerDataState({});

  const setProposalData = (data: Partial<ProposalData>) => {
    setProposalDataState((prev) => ({ ...prev, ...data }));
  };

  const clearProposalData = () => setProposalDataState({});

  const setAuthorization = (data: Partial<Authorization>) => {
    setAuthorizationState((prev) => ({ ...prev, ...data }));
  };

  const clearAuthorization = () => setAuthorizationState({});

  return (
    <C6BankContext.Provider
      value={{
        customerData,
        setCustomerData,
        clearCustomerData,
        proposalData,
        setProposalData,
        clearProposalData,
        authorization,
        setAuthorization,
        clearAuthorization,
      }}
    >
      {children}
    </C6BankContext.Provider>
  );
};

export const useC6Bank = (): C6BankContextType => {
  const context = useContext(C6BankContext);
  if (!context)
    throw new Error("useC6Bank must be used within a C6BankProvider");
  return context;
};
